import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { toast } from "@/components/ui/use-toast";
import { phpApi } from "@/integrations/php/client";
import { usePartners } from "@/context/PartnerContext";
import { Loader2, LogIn, Users, ShieldCheck, Zap, CheckCircle2 } from "lucide-react";

const formSchema = z.object({
  email: z.string().email({
    message: "Παρακαλώ εισάγετε ένα έγκυρο email.",
  }),
  phone: z.string().min(10, {
    message: "Το τηλέφωνο πρέπει να έχει τουλάχιστον 10 ψηφία.",
  }),
});

const PartnerLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { partners } = usePartners();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      phone: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);
    
    try {
      // Find the partner with matching email and phone
      const matchedPartner = partners.find(
        (partner) => 
          partner.email.toLowerCase() === values.email.toLowerCase() && 
          partner.phone === values.phone
      );
      
      if (matchedPartner) {
        // Store partner ID in session storage for temporary access
        sessionStorage.setItem("partnerId", matchedPartner.id);
        sessionStorage.setItem("partnerName", matchedPartner.name);
        
        // We'll skip updating the last_login time for now as the field doesn't exist in the schema
        
        toast({
          title: "Επιτυχής σύνδεση!",
          description: `Καλωσήρθατε, ${matchedPartner.name}`,
        });
        
        // Redirect to partner dashboard
        navigate(`/partner/${matchedPartner.id}/dashboard`);
      } else {
        toast({
          title: "Σφάλμα σύνδεσης",
          description: "Τα στοιχεία που εισάγατε δεν αντιστοιχούν σε κάποιον συνεργάτη.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      toast({
        title: "Σφάλμα σύνδεσης",
        description: "Προέκυψε σφάλμα κατά τη σύνδεση. Παρακαλώ δοκιμάστε ξανά.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl" />

        <div className="flex items-center gap-3 relative z-10">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-500 shadow-lg shadow-emerald-500/25">
            <Users className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Partner Portal</h1>
            <p className="text-xs text-emerald-300">Διαθερμική</p>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <h2 className="text-4xl font-bold text-white leading-tight">
            Το δίκτυο<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-teal-300">
              συνεργατών σας
            </span>
          </h2>
          <p className="text-lg text-emerald-200 max-w-md">
            Δείτε τις αναθέσεις σας, ενημερώστε τα στάδια lead και παρακολουθήστε την απόδοσή σας.
          </p>
          <div className="space-y-3 pt-2">
            {[
              "Άμεση ενημέρωση αναθέσεων",
              "Παρακολούθηση σταδίων",
              "Αναλυτικά στατιστικά",
            ].map((item) => (
              <div key={item} className="flex items-center gap-2 text-emerald-200">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                <span className="text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-sm text-emerald-500 relative z-10">
          © {new Date().getFullYear()} Διαθερμική. Όλα τα δικαιώματα διατηρούνται.
        </p>
      </div>

      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 via-white to-emerald-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center justify-center gap-3 mb-8 lg:hidden">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600">
              <Users className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">Partner Portal</span>
          </div>

          <Card className="border-0 shadow-xl shadow-slate-200/50">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-center text-slate-900">
                Σύνδεση Συνεργάτη
              </CardTitle>
              <CardDescription className="text-center text-slate-500">
                Εισάγετε το email και το τηλέφωνό σας
              </CardDescription>
            </CardHeader>

            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700">Email</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="your@email.com"
                            className="h-11 bg-slate-50 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-slate-700">Τηλέφωνο</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="6912345678"
                            className="h-11 bg-slate-50 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500/20"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button
                    type="submit"
                    className="w-full h-11 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg shadow-emerald-500/25"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Σύνδεση...
                      </>
                    ) : (
                      <>
                        <LogIn className="mr-2 h-4 w-4" />
                        Σύνδεση
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>

            <CardFooter className="flex justify-center border-t pt-4">
              <div className="flex items-center gap-1.5 text-xs text-slate-500">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
                Επικοινωνήστε με τον διαχειριστή εάν έχετε πρόβλημα σύνδεσης
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PartnerLogin;
